import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { getActiveLensForSong, ActiveLens } from './explorerLensService';
import { getLensFocus, setLensFocus, clearLensFocus, ExplorerLensFocus } from './explorerLensFocusService';

interface ExplorerContextType {
  activeLens: ActiveLens | null;
  loadActiveLens: (songId: string) => Promise<void>;
  setActiveLensState: (lens: ActiveLens | null) => void;
  isLoading: boolean;
  lensFocus: ExplorerLensFocus | null;
  loadLensFocus: (songId: string) => Promise<void>;
  activateLensFocus: (songId: string, lensCategory: string, archetypeId: string) => Promise<boolean>;
  clearLensFocusState: (songId: string) => Promise<boolean>;
}

const ExplorerContext = createContext<ExplorerContextType | undefined>(undefined);

export function ExplorerProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeLens, setActiveLens] = useState<ActiveLens | null>(null);
  const [lensFocus, setLensFocusState] = useState<ExplorerLensFocus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadActiveLens = useCallback(async (songId: string) => {
    if (!user) {
      setActiveLens(null);
      return;
    }

    setIsLoading(true);
    try {
      const lens = await getActiveLensForSong(user.id, songId);
      setActiveLens(lens);
    } catch (error) {
      console.error('[ExplorerContext] Error loading active lens:', error);
      setActiveLens(null);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const setActiveLensState = useCallback((lens: ActiveLens | null) => {
    setActiveLens(lens);
  }, []);

  const loadLensFocus = useCallback(async (songId: string) => {
    if (!user) {
      setLensFocusState(null);
      return;
    }

    try {
      const focus = await getLensFocus(user.id, songId);
      setLensFocusState(focus);
    } catch (error) {
      console.error('[ExplorerContext] Error loading lens focus:', error);
      setLensFocusState(null);
    }
  }, [user]);

  const activateLensFocus = useCallback(async (
    songId: string,
    lensCategory: string,
    archetypeId: string
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const focus = await setLensFocus(user.id, songId, lensCategory, archetypeId);
      if (focus) {
        setLensFocusState(focus);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ExplorerContext] Error activating lens focus:', error);
      return false;
    }
  }, [user]);

  const clearLensFocusState = useCallback(async (songId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      const success = await clearLensFocus(user.id, songId);
      if (success) {
        setLensFocusState(null);
        return true;
      }
      return false;
    } catch (error) {
      console.error('[ExplorerContext] Error clearing lens focus:', error);
      return false;
    }
  }, [user]);

  return (
    <ExplorerContext.Provider
      value={{
        activeLens,
        loadActiveLens,
        setActiveLensState,
        isLoading,
        lensFocus,
        loadLensFocus,
        activateLensFocus,
        clearLensFocusState,
      }}
    >
      {children}
    </ExplorerContext.Provider>
  );
}

export function useExplorer() {
  const context = useContext(ExplorerContext);
  if (context === undefined) {
    throw new Error('useExplorer must be used within an ExplorerProvider');
  }
  return context;
}
